import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { journalCapitalContribution, journalOwnerWithdrawal, postJournal } from '../lib/journalEngine'

const ugandaToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' })

const ENTRY_TYPES = [
  { value: 'Capital Contribution', label: 'Capital Contribution (Cash)', icon: '💵', desc: 'Cash injected by owner without issuing new shares. Updates cash balance. No URSB filing needed.' },
  { value: 'Share Issuance', label: 'New Share Issuance', icon: '📜', desc: 'New shares issued to a shareholder. Records par value to Share Capital and excess to Share Premium. Requires URSB filing.' },
  { value: 'Contribution in Kind', label: 'Contribution in Kind', icon: '🔧', desc: 'Non-cash contribution — expenses paid personally on behalf of the company (fuel, subscriptions). No cash movement.' },
  { value: 'Dividends', label: 'Dividend Payout', icon: '💰', desc: 'Formal dividend payment from retained earnings. Requires board resolution. Dr Retained Earnings / Cr Cash.' },
  { value: 'Owner Withdrawal', label: 'Owner Withdrawal', icon: '↩️', desc: 'Informal cash taken out by the owner. Reduces cash balance. Different from dividends — no board resolution required.' },
]

const KIND_EXPENSE_ACCOUNTS = [
  { code: '5200', label: 'Nutrients & Chemicals (5200)' },
  { code: '6200', label: 'Electricity (6200)' },
  { code: '6300', label: 'Fuel & Vehicle (6300)' },
  { code: '6400', label: 'Equipment Repairs (6400)' },
  { code: '6500', label: 'Rent (6500)' },
  { code: '6700', label: 'Marketing & Delivery (6700)' },
  { code: '6800', label: 'Professional Fees / Subscriptions (6800)' },
  { code: '6850', label: 'Technology & Software (6850)' },
  { code: '6900', label: 'Bank Charges (6900)' },
  { code: '9000', label: 'Miscellaneous (9000)' },
]

const typeColor = {
  'Share Issuance': 'badge-blue',
  'Dividends': 'badge-red',
  'Capital Contribution': 'badge-green',
  'Contribution in Kind': 'badge-blue',
  'Owner Withdrawal': 'badge-red',
}

const fmt = (n) => Math.round(n || 0).toLocaleString()

export default function Equity() {
  const { user, profile, isAdmin } = useAuth()
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  const emptyForm = {
    entry_date: ugandaToday(),
    type: 'Capital Contribution',
    contributor: profile?.full_name || '',
    amount: '',
    payment_method: 'Bank Transfer',
    reference: '',
    notes: '',
    expense_account: '6300', // for Contribution in Kind
    shares_issued: '',
    par_value: '1000',
  }
  const [form, setForm] = useState(emptyForm)

  useEffect(() => { loadEntries() }, [])

  const loadEntries = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('equity_entries')
      .select('*, profiles(full_name)')
      .order('entry_date', { ascending: false })
    setEntries(data || [])
    setLoading(false)
  }

  const handleSave = async () => {
    if (!form.contributor || !form.amount) {
      setMsg({ type: 'error', text: 'Contributor name and amount are required.' }); return
    }
    setSaving(true); setMsg(null)

    const amt = parseFloat(form.amount)

    const { error } = await supabase.from('equity_entries').insert({
      user_id: user.id,
      entry_date: form.entry_date,
      type: form.type,
      contributor: form.contributor,
      amount: amt,
      payment_method: form.type === 'Contribution in Kind' ? 'In Kind' : form.payment_method,
      reference: form.reference,
      notes: form.notes,
    })
    if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return }

    // Get saved entry ID
    const { data: savedEntry } = await supabase.from('equity_entries')
      .select('id').eq('contributor', form.contributor).eq('entry_date', form.entry_date)
      .order('created_at', { ascending: false }).limit(1).single()
    const entryId = savedEntry?.id || Date.now().toString()

    if (form.type === 'Capital Contribution') {
      // Dr 1101 Cash / Cr 3300 Capital Contributions
      await journalCapitalContribution({
        entryId, contributor: form.contributor, amount: amt,
        date: form.entry_date, userId: user.id,
      })
      await supabase.from('cash_logs').insert({
        user_id: user.id, log_date: form.entry_date,
        total_income: amt, total_expenses: 0, net: amt,
        income_rows: [{ type: 'equity', description: `Capital contribution — ${form.contributor}`, total: amt }],
        expense_rows: [],
        notes: `Capital contribution by ${form.contributor}. Ref: ${form.reference || '—'}`,
      })

    } else if (form.type === 'Contribution in Kind') {
      // Dr expense account / Cr 3300 Capital Contributions (NO cash movement)
      const accountLabel = KIND_EXPENSE_ACCOUNTS.find(a => a.code === form.expense_account)?.label || form.expense_account
      await postJournal({
        journalId: `JNL-KIND-${entryId}`,
        date: form.entry_date,
        module: 'equity',
        reference: entryId,
        debitAccount: form.expense_account,
        creditAccount: '3300',
        amount: amt,
        description: `Contribution in kind — ${form.contributor} — ${accountLabel} — ${form.notes || ''}`,
        sourceId: entryId,
        userId: user.id,
      })

    } else if (form.type === 'Share Issuance') {
      // Dr 1101 Cash / Cr 3100 Share Capital (par) + Cr 3201 Share Premium (excess)
      const sharesIssued = parseFloat(form.shares_issued) || 0
      const parValue = parseFloat(form.par_value) || 1000
      const parTotal = sharesIssued * parValue
      const premium = Math.max(0, amt - parTotal)
      // Post par value to Share Capital
      if (parTotal > 0) {
        await postJournal({
          journalId: `JNL-SHARECAP-NEW-${entryId}`,
          date: form.entry_date, module: 'equity', reference: entryId,
          debitAccount: '1101', creditAccount: '3100',
          amount: parTotal,
          description: `Share issuance — ${sharesIssued} shares @ UGX ${parValue} par — ${form.contributor}`,
          sourceId: entryId, userId: user.id,
        })
      }
      // Post share premium
      if (premium > 0) {
        await postJournal({
          journalId: `JNL-SHAREPREM-${entryId}`,
          date: form.entry_date, module: 'equity', reference: entryId,
          debitAccount: '1101', creditAccount: '3201',
          amount: premium,
          description: `Share premium — ${form.contributor} — ${sharesIssued} shares @ UGX ${(amt/sharesIssued).toFixed(0)} issue price`,
          sourceId: entryId, userId: user.id,
        })
      }
      // Cash log
      await supabase.from('cash_logs').insert({
        user_id: user.id, log_date: form.entry_date,
        total_income: amt, total_expenses: 0, net: amt,
        income_rows: [{ type: 'equity', description: `Share issuance — ${form.contributor} (${sharesIssued} shares)`, total: amt }],
        expense_rows: [],
        notes: `Share issuance: ${sharesIssued} shares at UGX ${(amt/sharesIssued).toFixed(0)} each. Par: UGX ${parTotal.toLocaleString()}, Premium: UGX ${premium.toLocaleString()}.`,
      })
    } else if (form.type === 'Dividends') {
      // Dr 3500 Retained Earnings / Cr 1101 Cash
      // Two-step: declaration + payment (simplified to direct for small company)
      if (!form.notes?.toLowerCase().includes('resolution') && !form.reference) {
        // Warn but don't block — small company can do this
      }
      await postJournal({
        journalId: `JNL-DIV-${entryId}`,
        date: form.entry_date, module: 'equity', reference: entryId,
        debitAccount: '3500',  // Retained Earnings
        creditAccount: '1101', // Cash
        amount: amt,
        description: `Dividend payout — ${form.contributor} — ${form.notes || form.reference || ''}`,
        sourceId: entryId, userId: user.id,
      })
      await supabase.from('cash_logs').insert({
        user_id: user.id, log_date: form.entry_date,
        total_income: 0, total_expenses: amt, net: -amt,
        income_rows: [],
        expense_rows: [{ type: 'equity', description: `Dividend payout — ${form.contributor}`, amount: amt }],
        notes: `Dividend paid to ${form.contributor}. Ref: ${form.reference || '—'}. ${form.notes || ''}`,
      })
    } else if (form.type === 'Owner Withdrawal') {
      // Dr 3400 Withdrawals / Cr 1101 Cash
      await journalOwnerWithdrawal({
        entryId, contributor: form.contributor, amount: amt,
        date: form.entry_date, userId: user.id,
      })
      await supabase.from('cash_logs').insert({
        user_id: user.id, log_date: form.entry_date,
        total_income: 0, total_expenses: amt, net: -amt,
        income_rows: [],
        expense_rows: [{ type: 'equity', description: `Owner withdrawal — ${form.contributor}`, amount: amt }],
        notes: `Owner withdrawal by ${form.contributor}. Ref: ${form.reference || '—'}`,
      })
    }

    setMsg({ type: 'success', text: `${form.type} of UGX ${fmt(amt)} recorded. Journal entry posted automatically.` })
    setSaving(false); setShowForm(false); setForm(emptyForm); loadEntries()
    setTimeout(() => setMsg(null), 4000)
  }

  const handleExportExcel = async () => {
    if (!window.XLSX) {
      await new Promise((resolve, reject) => {
        const s = document.createElement('script')
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js'
        s.onload = resolve; s.onerror = reject
        document.head.appendChild(s)
      })
    }
    const XLSX = window.XLSX
    const wb = XLSX.utils.book_new()

    const rows = [
      ['Kobflo Greentek Ltd — Equity & Capital Statement'],
      [`Generated: ${ugandaToday()}`],
      [],
      ['Date', 'Type', 'Contributor', 'Amount (UGX)', 'Method', 'Reference', 'Notes', 'Recorded By'],
      ...entries.map(e => [
        e.entry_date, e.type, e.contributor, e.amount,
        e.payment_method || '', e.reference || '', e.notes || '',
        e.profiles?.full_name || '',
      ]),
      [],
      ['Total Cash Contributions', totalCash],
      ['Total Share Issuances', totalShares],
      ['Total In-Kind Contributions', totalKind],
      ['Total Dividends Paid', totalDividends],
      ['Total In-Kind Contributions', totalKind],
      ['Total Withdrawals', totalWithdrawals],
      ['Net Capital Contributions', totalCash + totalKind - totalWithdrawals],
    ]

    const ws = XLSX.utils.aoa_to_sheet(rows)
    ws['!cols'] = [{ wch:12 },{ wch:24 },{ wch:22 },{ wch:18 },{ wch:16 },{ wch:16 },{ wch:40 },{ wch:20 }]
    ws['!merges'] = [{ s:{r:0,c:0}, e:{r:0,c:7} }, { s:{r:1,c:0}, e:{r:1,c:7} }]
    XLSX.utils.book_append_sheet(wb, ws, 'Equity Statement')
    XLSX.writeFile(wb, `Kobflo_Equity_${ugandaToday()}.xlsx`)
  }

  const totalCash = entries.filter(e => e.type === 'Capital Contribution').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
  const totalShares = entries.filter(e => e.type === 'Share Issuance').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
  const totalKind = entries.filter(e => e.type === 'Contribution in Kind').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
  const totalDividends = entries.filter(e => e.type === 'Dividends').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
  const totalWithdrawals = entries.filter(e => e.type === 'Owner Withdrawal').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
  const netEquity = totalCash + totalShares + totalKind - totalWithdrawals - totalDividends

  return (
    <AppLayout title="Equity & Capital">
      <div className="section-header">
        <div className="section-icon">🏦</div>
        <div className="section-info">
          <h2>Equity & Capital</h2>
          <p>Record capital injections, in-kind contributions, and owner withdrawals. All entries post journal entries automatically.</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setShowForm(s => !s)}>
            + Record Entry
          </button>
        )}
      </div>

      {msg && <div className={`alert ${msg.type === 'success' ? 'alert-green' : 'alert-red'}`}>{msg.text}</div>}

      {/* Summary KPIs */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi">
          <div className="kpi-label">Cash Contributions</div>
          <div className="kpi-value" style={{ color: 'var(--green-mid)' }}>UGX {fmt(totalCash)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Share Issuances</div>
          <div className="kpi-value" style={{ color: 'var(--green-mid)' }}>UGX {fmt(totalShares)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">In-Kind Contributions</div>
          <div className="kpi-value" style={{ color: 'var(--blue)' }}>UGX {fmt(totalKind)}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Expenses paid personally</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Owner Withdrawals</div>
          <div className="kpi-value" style={{ color: 'var(--red)' }}>(UGX {fmt(totalWithdrawals)})</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Dividends Paid</div>
          <div className="kpi-value" style={{ color: 'var(--red)' }}>(UGX {fmt(totalDividends)})</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>From retained earnings</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Net Capital Contributions</div>
          <div className="kpi-value" style={{ color: netEquity >= 0 ? 'var(--green-deep)' : 'var(--red)', fontWeight: 900 }}>
            UGX {fmt(netEquity)}
          </div>
        </div>
      </div>

      {/* Entry form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 16, border: '2px solid var(--amber)' }}>
          <div className="card-title">Record Equity Entry</div>

          {/* Entry type selector */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
            {ENTRY_TYPES.map(t => (
              <div
                key={t.value}
                onClick={() => setForm(f => ({ ...f, type: t.value }))}
                style={{
                  padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
                  border: `2px solid ${form.type === t.value ? 'var(--green-bright)' : 'var(--border)'}`,
                  background: form.type === t.value ? 'var(--green-pale)' : 'var(--cream)',
                }}
              >
                <div style={{ fontSize: '1.2rem', marginBottom: 4 }}>{t.icon}</div>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 3 }}>{t.label}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{t.desc}</div>
              </div>
            ))}
          </div>

          <div className="grid-2" style={{ marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input className="form-input" type="date" value={form.entry_date} onChange={e => setForm(f => ({ ...f, entry_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Contributor / Name *</label>
              <input className="form-input" value={form.contributor} onChange={e => setForm(f => ({ ...f, contributor: e.target.value }))} placeholder="e.g. Edward Katongole" />
            </div>
            <div className="form-group">
              <label className="form-label">Amount (UGX) *</label>
              <input className="form-input" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" />
            </div>

            {form.type === 'Contribution in Kind' ? (
              <div className="form-group">
                <label className="form-label">Expense Category (what was paid for)</label>
                <select className="form-select" value={form.expense_account} onChange={e => setForm(f => ({ ...f, expense_account: e.target.value }))}>
                  {KIND_EXPENSE_ACCOUNTS.map(a => <option key={a.code} value={a.code}>{a.label}</option>)}
                </select>
              </div>
            ) : form.type === 'Share Issuance' ? (
              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <select className="form-select" value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}>
                  {['Bank Transfer', 'Cash', 'Mobile Money', 'Cheque'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <select className="form-select" value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}>
                  {['Bank Transfer', 'Cash', 'Mobile Money', 'Cheque'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
            )}
            {form.type === 'Dividends' && (
            <div className="alert alert-amber" style={{ marginBottom: 12, fontSize: '0.78rem' }}>
              ℹ️ <strong>Dividend payout:</strong> Posts Dr 3500 Retained Earnings / Cr 1101 Cash. Can only be paid from accumulated profits. Use the Reference field for board resolution number and Notes for the resolution date. Dividends reduce retained earnings, not share capital.
            </div>
          )}
          {form.type === 'Share Issuance' && (
              <>
                <div className="form-group">
                  <label className="form-label">Number of Shares Issued *</label>
                  <input className="form-input" type="number" value={form.shares_issued || ''} onChange={e => setForm(f => ({ ...f, shares_issued: e.target.value }))} placeholder="e.g. 1000" />
                </div>
                <div className="form-group">
                  <label className="form-label">Par Value per Share (UGX)</label>
                  <input className="form-input" type="number" value={form.par_value || '1000'} onChange={e => setForm(f => ({ ...f, par_value: e.target.value }))} placeholder="1000" />
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    Par value: UGX {((parseFloat(form.shares_issued)||0) * (parseFloat(form.par_value)||1000)).toLocaleString()} · 
                    Share Premium: UGX {Math.max(0,(parseFloat(form.amount)||0) - ((parseFloat(form.shares_issued)||0) * (parseFloat(form.par_value)||1000))).toLocaleString()}
                  </div>
                </div>
              </>
            )}

            <div className="form-group">
              <label className="form-label">Reference / Transaction #</label>
              <input className="form-input" value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="e.g. TXN-12345" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes {form.type === 'Contribution in Kind' ? '(describe what was purchased)' : ''}</label>
            <textarea className="form-textarea" style={{ minHeight: 60 }} value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder={form.type === 'Contribution in Kind' ? 'e.g. Fuel for farm vehicles — March 2026, Claude Pro subscription — Feb 2026' : ''} />
          </div>

          {form.type === 'Contribution in Kind' && (
            <div className="alert alert-green" style={{ marginBottom: 12, fontSize: '0.78rem' }}>
              ℹ️ <strong>In-Kind contribution:</strong> Posts Dr {form.expense_account} / Cr 3300 Capital Contributions. No cash movement — the expense hits the P&L and your contribution is recorded in equity.
            </div>
          )}
          {form.type === 'Dividends' && (
            <div className="alert alert-amber" style={{ marginBottom: 12, fontSize: '0.78rem' }}>
              ℹ️ <strong>Dividend payout:</strong> Posts Dr 3500 Retained Earnings / Cr 1101 Cash. Can only be paid from accumulated profits. Use the Reference field for board resolution number and Notes for the resolution date. Dividends reduce retained earnings, not share capital.
            </div>
          )}
          {form.type === 'Share Issuance' && (
            <div className="alert alert-green" style={{ marginBottom: 12, fontSize: '0.78rem' }}>
              ℹ️ <strong>New share issuance:</strong> Posts Dr 1101 Cash / Cr 3100 Share Capital (par value) + Cr 3201 Share Premium (excess above par). Updates cash balance. Remember to file with URSB and issue share certificates.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : '💾 Save Entry'}
            </button>
            <button className="btn btn-ghost" onClick={() => { setShowForm(false); setForm(emptyForm) }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Entries table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div className="card-title">Equity Entry History</div>
            <div className="card-subtitle">All capital contributions, in-kind contributions and withdrawals.</div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={handleExportExcel}>📊 Export Excel</button>
        </div>

        {loading ? (
          <div className="empty-state"><p>Loading...</p></div>
        ) : entries.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🏦</div>
            <h3>No equity entries yet</h3>
            <p>Record capital contributions or owner withdrawals using the button above.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Contributor</th>
                  <th style={{ textAlign: 'right' }}>Amount (UGX)</th>
                  <th>Method</th>
                  <th>Notes</th>
                  <th>Recorded By</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id}>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {new Date(e.entry_date + 'T00:00:00').toLocaleDateString('en-UG')}
                    </td>
                    <td>
                      <span className={`badge ${typeColor[e.type] || 'badge-grey'}`}>{e.type}</span>
                    </td>
                    <td style={{ fontWeight: 500 }}>{e.contributor}</td>
                    <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontWeight: 700,
                      color: e.type === 'Owner Withdrawal' ? 'var(--red)' : 'var(--green-mid)' }}>
                      {e.type === 'Owner Withdrawal' ? '(' : ''}{fmt(e.amount)}{e.type === 'Owner Withdrawal' ? ')' : ''}
                    </td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{e.payment_method || '—'}</td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)', maxWidth: 250 }}>
                      {e.reference ? <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.72rem' }}>{e.reference} · </span> : ''}
                      {e.notes || '—'}
                    </td>
                    <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{e.profiles?.full_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--green-pale)', fontWeight: 700 }}>
                  <td colSpan={3} style={{ padding: '10px 12px' }}>Net Capital Contributions</td>
                  <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", padding: '10px 12px',
                    color: netEquity >= 0 ? 'var(--green-deep)' : 'var(--red)' }}>
                    UGX {fmt(netEquity)}
                  </td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
